/**
 * The Agent Operating Procedure plan, mirroring openwrench-intelligence-service.
 *
 * Source of truth: `domain/plan.py` and `domain/vocabulary.py` on
 * emptor-ai/openwrench-intelligence-service#20. Field names are the wire names
 * (snake_case) on purpose — the plan is a document we round-trip untouched, and
 * a camelCase mapping layer would be one more place for it to drift.
 *
 * The plan is a flat list of steps and the graph is explicit:
 *   - `operate` and `decision` steps carry `exits`, each naming a target step
 *   - a `communicate` step carries exactly one, `after`, taken once its message
 *     is sent — so list order is presentation, not control flow
 *   - a `finish` step goes nowhere
 *   - the procedure enters at `steps[0]`
 *
 * There are no coordinates here. Canvas positions are derived (see
 * AOPLayoutUtils) and are not persisted.
 */

/* -------------------------------------------------------------------------- */
/*                                 Vocabulary                                 */
/* -------------------------------------------------------------------------- */

/**
 * The only names a plan may use. Append-only upstream: a name may be added but
 * never removed or redefined, so anything not listed here is a plan Radar
 * cannot run.
 */

export type AOPParty = 'supplier' | 'buyer' | 'vfa';
export type AOPChannel = 'note';
export type AOPOperation = 'reassign_supplier';
export type AOPTrigger = 'note_added' | 'status_changed';
/**
 * How a plan ends. Two words, because a plan only has to say whether the job
 * got done — the detail of *how* is in the steps that led here.
 *
 * A run has a third, `cancelled`, which a plan cannot name: cancelling is
 * something done to a run from outside it, never something an author writes.
 * See `AOPExecutionOutcome`.
 */
export type AOPOutcome = 'success' | 'failure';

/** Reserved exit names. Every other exit name is an answer to the question. */
export const AOP_ON_TIMEOUT = 'on_timeout';
export const AOP_ON_EXHAUSTED = 'on_exhausted';

/** Every block takes incoming edges on this one target handle. */
export const AOP_IN_HANDLE = 'in';
/**
 * The one exit a `communicate` step carries, taken once its message is sent.
 * It is a real exit with a real target: dragging it retargets the step, and a
 * communicate step can point backwards like any other.
 */
export const AOP_AFTER_EXIT = 'after';

/** The source handle of the Start block, which is drawn but is not a step. */
export const AOP_OUT_HANDLE = 'out';

export const AOP_MAX_DESCRIPTION = 120;

/**
 * What a decision's question may run to. Its own limit, four times the
 * description's, because a question is the thing a supplier is actually asked.
 */
export const AOP_MAX_QUESTION = 500;
export const AOP_MAX_SECONDS = 7 * 24 * 60 * 60;
export const AOP_MAX_EXITS = 8;
export const AOP_MAX_ESCALATIONS = 5;
export const AOP_MAX_STEPS = 100;

/* -------------------------------------------------------------------------- */
/*                                    Steps                                   */
/* -------------------------------------------------------------------------- */

/**
 * A step's `name`: snake_case, unique in the plan, and what every exit points
 * at. It is an identifier that happens to be readable, not a title — the title
 * is `description`.
 */
export type AOPStepName = string;

/** What a step's `name` has to match: `^[a-z][a-z0-9_]*$`, up to 60. */
export const AOP_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
export const AOP_MAX_NAME = 60;

export interface AOPExit {
	/** Lowercase snake_case; unique within its step. */
	name: string;
	/** The `name` of the step to continue at. */
	target: AOPStepName;
}

export interface AOPTransmission {
	party: AOPParty;
	channel: AOPChannel;
	message: string;
}

export interface AOPEscalation extends AOPTransmission {
	window_seconds: number;
}

export interface AOPStepBase {
	/** Identifies the step; exits and finding anchors point at this. */
	name: AOPStepName;
	/** A sentence, 1-120 characters. What every screen shows as the title. */
	description: string;
}

export interface AOPCommunicateStep extends AOPStepBase, AOPTransmission {
	type: 'communicate';
	/** Exactly one, named `after`. Required: there is no falling through. */
	exits: AOPExit[];
}

/** One entry of an operate step's own supplier list. */
export interface AOPListedSupplier {
	supplier_facility_id: number;
}

export interface AOPOperateStep extends AOPStepBase {
	type: 'operate';
	operation: AOPOperation;
	/**
	 * Suppliers to try in order instead of OpenWrench's ranked list, up to ten.
	 * Left out when empty, and the compiler never writes one — a list is set by
	 * editing the plan. Nothing in the builder offers one yet, so this is
	 * carried through untouched rather than dropped on save.
	 */
	suppliers?: AOPListedSupplier[];
	/** Must be exactly the operation's declared results — no more, no fewer. */
	exits: AOPExit[];
}

export interface AOPDecisionStep extends AOPStepBase {
	type: 'decision';
	question: string;
	timeout_seconds: number;
	/** Both optional upstream; absent reads the same as empty. */
	triggers?: AOPTrigger[];
	escalations?: AOPEscalation[];
	/**
	 * One exit per answer, plus exactly one of `on_timeout` (no escalations) or
	 * `on_exhausted` (escalations present) — never both.
	 */
	exits: AOPExit[];
}

export interface AOPFinishStep extends AOPStepBase {
	type: 'finish';
	outcome: AOPOutcome;
}

export type AOPStep = AOPCommunicateStep | AOPOperateStep | AOPDecisionStep | AOPFinishStep;

export type AOPStepType = AOPStep['type'];

export interface AOPPlan {
	steps: AOPStep[];
}

/* -------------------------------------------------------------------------- */
/*                                  Findings                                  */
/* -------------------------------------------------------------------------- */

/**
 * A detail the compiler filled in because the description did not say: a wait
 * with no duration given, an ending not described, an assumption it had to
 * make. The author rules on each one — a draft is not publishable while any is
 * still `open`.
 *
 * `added` marks a suggestion that introduced a step rather than a value within
 * one. `removed` means its step has since been deleted, so there is nothing
 * left to rule on. `message` is prose written by the compiler; there is no
 * structured value behind it, which is why a suggestion is read and accepted
 * rather than edited in place.
 */
export interface AOPSuggestion {
	id: string;
	/** The step it is about, or null when it is about the plan. */
	anchor: string | null;
	added: boolean;
	message: string;
	status: 'open' | 'accepted' | 'removed';
}

/**
 * A rule the plan breaks, derived by the service on every read. Unlike a
 * suggestion there is nothing to accept: the plan has to change.
 */
export type AOPViolationRule =
	| 'unsupported_step'
	| 'missing_exit'
	| 'no_answer_exit'
	| 'extra_exit'
	| 'duplicate_exit'
	| 'dangling_exit'
	| 'unreachable_step'
	| 'dead_end'
	| 'never_finishes'
	| 'false_success'
	| 'duplicate_step'
	| 'unknown_placeholder'
	| 'past_time_limit';

/**
 * What a rule carries beyond its name. Which keys appear depends on the rule:
 *
 *   unsupported_step                                    request, reason
 *   unknown_placeholder                                 placeholders
 *   missing_exit, extra_exit, duplicate_exit,
 *   dangling_exit                                       exits
 *   false_success                                       exit
 *   past_time_limit                                     waited_seconds,
 *                                                       max_run_seconds
 *   everything else                                     nothing
 */
export interface AOPViolationParams {
	request?: string;
	reason?: AOPUnsupportedReason;
	placeholders?: string[];
	exits?: string[];
	exit?: string;
	waited_seconds?: number;
	max_run_seconds?: number;
}

/**
 * A rule the plan breaks, derived by the service on every read.
 *
 * The service sends the rule and the facts behind it and writes no prose — it
 * carries no step names either — so the sentence a reader sees is ours, built
 * in `AOPViolationUtils`. That is what lets a violation about an exit name it
 * the way the rest of the canvas does.
 */
export interface AOPViolation {
	rule: AOPViolationRule;
	params?: AOPViolationParams;
	/** The step that breaks the rule, or null for the plan as a whole. */
	anchor?: string | null;
}

/**
 * Something the author asked for that no term covers — a phone call, an email.
 * It stands in the plan carrying what they asked for and one to three runnable
 * alternatives to swap in, so the request is answered rather than dropped.
 *
 * A draft may hold these; a published plan may not, so one always comes with an
 * `unsupported_step` violation.
 */
/**
 * Which part of the vocabulary the request fell outside of. Written by the
 * compiler, and named in the step's `unsupported_step` violation — so the
 * message says what is wrong and this says what kind of thing it is.
 */
export type AOPUnsupportedReason = 'channel' | 'party' | 'operation' | 'trigger' | 'other';

export interface AOPUnsupportedStep {
	name: AOPStepName;
	description: string;
	type: 'unsupported';
	/** What the author asked for, in their words. */
	request: string;
	reason: AOPUnsupportedReason;
	alternatives: AOPStep[];
}

/** What a draft's plan may contain; a published plan may not hold the last. */
export type AOPDraftStep = AOPStep | AOPUnsupportedStep;

export interface AOPDraftPlan {
	steps: AOPDraftStep[];
}

export const isUnsupportedStep = (step: AOPDraftStep): step is AOPUnsupportedStep =>
	step.type === 'unsupported';

/* -------------------------------------------------------------------------- */
/*                            Procedure & draft                               */
/* -------------------------------------------------------------------------- */

export interface AOPProcedure {
	id: string;
	kind: 'dispatch';
	name: string;
	description: string | null;
	published_version: number | null;
	created_at: string;
	created_by: string;
	updated_at: string;
	updated_by: string;
}

export interface AOPDraft {
	procedure: AOPProcedure;
	prose: string | null;
	plan: AOPDraftPlan | null;
	/**
	 * How long a run of this draft may take in all, in seconds.
	 *
	 * Typed because the service sends it — required and never null — and the
	 * thunk spreads the whole document into the store, so it has been arriving
	 * all along with nothing naming it. Nothing reads it yet: there is no field
	 * to set it and no sentence showing it, so an author meets the limit only
	 * when a draft is refused with `past_time_limit` for exceeding it.
	 *
	 * Not on the procedure, despite being described as procedure-level: it sits
	 * on the draft and on each version cut from it.
	 */
	max_run_seconds: number;
	suggestions: AOPSuggestion[];
	violations: AOPViolation[];
	/** No suggestion still open and no violation. The service decides this. */
	publishable: boolean;
}

/**
 * A published version: immutable, numbered from 1, cut from whatever the draft
 * held at the moment of publishing. The summary is what the version list
 * returns; reading one version adds the document it froze.
 *
 * Versions carry no findings — they validated when they were published, and the
 * service only re-validates a draft.
 */
export interface AOPVersionSummary {
	version: number;
	change_note: string | null;
	published_by: string;
	published_at: string;
}

export interface AOPVersion extends AOPVersionSummary {
	procedure_id: string;
	prose: string;
	plan: AOPPlan;
	/** The limit the version was cut with. Carried, not shown — see `AOPDraft`. */
	max_run_seconds: number;
}

/* -------------------------------------------------------------------------- */
/*                          Scenarios & active window                         */
/* -------------------------------------------------------------------------- */

/** Either every problem type / priority, or an explicit set of ids (1-50). */
export type AOPScope = 'all' | number[];

export type AOPNamedWindowType = 'all_hours' | 'operating_hours' | 'after_hours';

export interface AOPNamedWindow {
	type: AOPNamedWindowType;
}

/**
 * `from` to `to` on each listed day, in the site's local time. ISO weekdays:
 * Monday is 1, Sunday is 7. A `to` before `from` ends the next day, and the two
 * may not be equal — a whole day is `00:00` to `24:00`.
 */
export interface AOPRange {
	days: number[];
	from: string;
	to: string;
}

/** Ranges may overlap; the active window is their union. 1-14 of them. */
export interface AOPCustomWindow {
	type: 'custom';
	ranges: AOPRange[];
}

export type AOPActiveWindow = AOPNamedWindow | AOPCustomWindow;

export const AOP_MAX_RANGES = 14;
export const AOP_MAX_SCOPE_IDS = 50;

export interface AOPScenario {
	id: string;
	procedure_id: string | null;
	problem_type_ids: AOPScope;
	priority_ids: AOPScope;
	active_window: AOPActiveWindow;
	created_at: string;
	created_by: string;
	updated_at: string;
	updated_by: string;
}

/**
 * What `GET /scenarios` returns. There is no endpoint that lists procedures, so
 * this is the only way to enumerate them — which is why the list page is
 * scenario-centric and a procedure with no scenario linked does not appear.
 */
/**
 * A live scenario a proposed scope would collide with, and what the two share.
 *
 * Two scenarios of one kind may not share a problem type, a priority and an
 * active window at once. Windows meet unless they cannot: `operating_hours`
 * and `after_hours` never do, two custom windows meet only where their minutes
 * of the week intersect, and any other pair meets — a procedure is location
 * agnostic, so the service cannot rule it out.
 */
export interface AOPScenarioConflict {
	scenario: AOPScenario;
	procedure: AOPProcedure;
	shared_problem_type_ids: AOPScope;
	shared_priority_ids: AOPScope;
}

export interface AOPScenarioEntry {
	scenario: AOPScenario;
	procedure: AOPProcedure | null;
}

/* -------------------------------------------------------------------------- */
/*                              Canvas view model                             */
/* -------------------------------------------------------------------------- */

export interface AOPPoint {
	x: number;
	y: number;
}

/** The palette groups blocks under four headings; the heading sets the accent. */
export type AOPPaletteGroup = 'lifecycle' | 'dispatch' | 'communications' | 'logic';

/** A drawn edge. Derived from the plan every render; never persisted. */
export interface AOPEdge {
	id: string;
	source: AOPStepName;
	sourceHandle: string;
	target: AOPStepName;
	targetHandle: string;
	className?: string;
	/**
	 * `aopRouted` for an edge that cannot take the straight way without crossing
	 * a card; absent for one that can, which React Flow draws as a smoothstep.
	 */
	type?: string;
	/** For a routed edge: the y of the channel it runs along, under the graph. */
	data?: { channelY: number };
}

/** An output row rendered on the right edge of a block. */
export interface AOPOutputDef {
	/** The exit name, and the React Flow source handle id. */
	id: string;
	/** Humanised for display, e.g. "Supplier list exhausted". */
	label: string;
	/** Muted prefix before the label, e.g. "≥ 45m ·" on a timeout exit. */
	condition: string;
	/** Muted suffix after it, e.g. "(>2h)" on a catch-all exit. */
	suffix?: string;
	/** The catch-all exit reads as a sentence, so it leads with a capital. */
	leading?: 'if' | 'If' | 'then';
	title: string;
}

/**
 * One node on the canvas. `step` is the plan step it draws; everything else is
 * derived and never persisted.
 */
export interface AOPCanvasNodeData {
	step: AOPDraftStep;
	/** 1-based position in `plan.steps`, shown top-right of the card. */
	stepNumber: number;
	/** Suggestions anchored to this step, and the rules it breaks. */
	suggestions: AOPSuggestion[];
	violations: AOPViolation[];
}

/** Procedure lifecycle as shown in the canvas header. */
export type AOPStatus = 'draft' | 'published';

/**
 * Which document the canvas is showing. `null` is the draft — the only one that
 * can be edited; a number is that published version, read-only.
 */
export type AOPSelectedVersion = number | null;
